import React from 'react';
import Isvg from 'react-inlinesvg';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import Dot from 'components/Dot';
import { DropdownItem, DropdownMenu, DropdownToggle } from 'components/Dropdown2';
import { Dropdown, Form } from 'components/HookForm';
import Message from 'components/Message';
import When from 'components/When';
import { ICONS } from 'constants/icons';
import { bem } from 'helpers/class-name';
import { selectIsMobile } from 'selectors/ui';

import {
  ITEM_AVAILABILITY_STATUS,
  ITEM_AVAILABILITY_STATUS_MAPPING,
  ITEM_AVAILABILITY_STATUS_OPTIONS,
} from '../Items.constants';
import { itemAvailabilityStatusMesages as messages, itemsMessages } from '../Items.messages';

import './ItemAvailabilityStatus.scss';

const { block, element } = bem('item-availability-status');
const FORM_NAME = 'item-availability-status-form';

const ItemAvailabilityStatusMarker = ({ className, status }) => {
  return status === ITEM_AVAILABILITY_STATUS.UNAVAILABLE_INDEFINITELY ? (
    <Isvg className={className} src={ICONS.noSymbol} />
  ) : (
    <Dot className={className} color={ITEM_AVAILABILITY_STATUS_MAPPING[status]?.color} largeSize />
  );
};

ItemAvailabilityStatusMarker.propTypes = {
  className: PropTypes.string,
  status: PropTypes.oneOf([...ITEM_AVAILABILITY_STATUS_OPTIONS, '']),
};

export const ItemAvailabilityStatusDropdown = ({
  handleEditItemFormOpen,
  isInEditForm,
  isPending,
  name,
  onChange: onChangeAvailabilityStatus,
  openDuplicateItem,
  showSettingsInMobile = false,
}) => {
  const isMobile = useSelector(selectIsMobile);

  return (
    <Dropdown
      className={element('dropdown')}
      name={name}
      disabled={isPending && !isMobile}
      isAlignTopToggleStyle
      isLargeRadiusStyle
      showMenuCaret>
      {({ closeMenu, onChange, value: availabilityStatus }) => {
        return (
          <>
            <DropdownToggle isRoundRadiusStyle>
              <When predicate={isPending}>
                <Isvg className={element('status-marker', { editForm: isInEditForm })} src={ICONS.clock} />
                <When predicate={!isMobile || isInEditForm}>
                  <Message message={messages.itemStatusPending} />
                </When>
              </When>
              <When predicate={!isPending}>
                <ItemAvailabilityStatusMarker
                  className={element('status-marker', { editForm: isInEditForm })}
                  status={availabilityStatus}
                />
                <When predicate={!isMobile || isInEditForm}>
                  <Message message={messages[ITEM_AVAILABILITY_STATUS_MAPPING[availabilityStatus]?.message]} />
                </When>
              </When>
            </DropdownToggle>
            <DropdownMenu className={element('dropdown-menu')}>
              {!isPending &&
                ITEM_AVAILABILITY_STATUS_OPTIONS.reduce((acc, option) => {
                  if (option === availabilityStatus) return [option, ...acc];
                  return [...acc, option];
                }, []).map(status => {
                  const { message } = ITEM_AVAILABILITY_STATUS_MAPPING[status];
                  return (
                    <DropdownItem
                      key={status}
                      isSelected={availabilityStatus === status}
                      onClick={() => {
                        onChange(status);
                        if (onChangeAvailabilityStatus) {
                          onChangeAvailabilityStatus(status);
                        }
                      }}>
                      <ItemAvailabilityStatusMarker className={element('dropdown-status-marker')} status={status} />
                      <Message message={messages[message]} />
                    </DropdownItem>
                  );
                })}
              <When predicate={showSettingsInMobile && isMobile}>
                <When predicate={isPending}>
                  <div className={element('pending-status-wrapper')}>
                    <Isvg className={element('dropdown-status-marker')} src={ICONS.clock} />
                    <Message message={messages.itemStatusPending} />
                  </div>
                </When>
                <div className={element('divider')} />
                <DropdownItem
                  onClick={() => {
                    openDuplicateItem();
                    closeMenu();
                  }}>
                  <span className={element('icon-wrapper')}>
                    <Isvg src={ICONS.copy} />
                  </span>
                  <Message message={itemsMessages.duplicateItem} />
                </DropdownItem>
                <DropdownItem
                  onClick={() => {
                    handleEditItemFormOpen();
                    closeMenu();
                  }}>
                  <span className={element('icon-wrapper')}>
                    <Isvg src={ICONS.editPencil} />
                  </span>
                  <Message message={itemsMessages.editItem} />
                </DropdownItem>
              </When>
            </DropdownMenu>
          </>
        );
      }}
    </Dropdown>
  );
};

ItemAvailabilityStatusDropdown.propTypes = {
  handleEditItemFormOpen: PropTypes.func,
  isInEditForm: PropTypes.bool,
  isPending: PropTypes.bool,
  name: PropTypes.string,
  onChange: PropTypes.func,
  showSettingsInMobile: PropTypes.bool,
  openDuplicateItem: PropTypes.func,
};

export const ItemAvailabilityStatusForm = ({
  availabilityStatus,
  handleEditItemFormOpen,
  isPending,
  onChange,
  openDuplicateItem,
}) => {
  return (
    <div className={block()}>
      <Form id={FORM_NAME} defaultValues={{ itemAvailabilityStatusInput: availabilityStatus }}>
        <ItemAvailabilityStatusDropdown
          name="itemAvailabilityStatusInput"
          handleEditItemFormOpen={handleEditItemFormOpen}
          isPending={isPending}
          onChange={onChange}
          openDuplicateItem={openDuplicateItem}
          showSettingsInMobile
        />
      </Form>
    </div>
  );
};

ItemAvailabilityStatusForm.propTypes = {
  availabilityStatus: PropTypes.oneOf(ITEM_AVAILABILITY_STATUS_OPTIONS),
  handleEditItemFormOpen: PropTypes.func,
  isPending: PropTypes.bool,
  onChange: PropTypes.func,
  openDuplicateItem: PropTypes.func,
};
